import React, { useState, useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useCan from '@shared/permissions/useCan'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import { Tag, Space, Tooltip, Modal, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import type { Permission } from '@/permissions_manifest.generated'

interface ProductRef {
  sku: string;
  name: string;
}

interface ProductsCellProps {
  products: ProductRef[] | null | undefined;
}

const ProductsCell: React.FC<ProductsCellProps> = ({ products }) => {
  if (!products || products.length === 0) {
    return <span className="text-muted">-</span>
  }
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {products.map((product, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '2px 0'
          }}
        >
          <Tooltip title={`SKU: ${product.sku}`}>
            <span style={{
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              marginRight: '8px'
            }}>
              {product.name}
            </span>
          </Tooltip>
        </div>
      ))}
    </Space>
  )
}

interface Merchant {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface FlexibleProductService {
  delete: (id: string) => Promise<unknown>;
  [key: string]: any;
}

interface FlexibleProductsListViewProps {
  role?: 'admin' | 'operator';
  tableId?: string;
  service: FlexibleProductService;
}

interface BatchAction {
  name: string;
  danger?: boolean;
  requiredPermission?: Permission;
  do: (selected: Iterable<string>) => void;
}

const FlexibleProductsListView: React.FC<FlexibleProductsListViewProps> = ({
  role = 'admin',
  tableId = 'FlexibleProductsTable',
  service,
}) => {
  const { t } = useTranslation()
  const can = useCan()
  const [lastModified, setLastModified] = useState(0)

  // Always call hooks unconditionally; only use the data when role === 'operator'
  const operatorData = useOperatorData() as { merchants?: Merchant[] } | null
  const merchants: Merchant[] = role === 'operator' ? (operatorData?.merchants || []) : []

  const getMechanismLabel = (mechanism: string) => {
    const mechanismMap: Record<string, string> = {
      'equally': t(`shared.flexible_products.mechanism.equally`),
      'most_stock': t(`shared.flexible_products.mechanism.most_stock`),
      'least_stock': t(`shared.flexible_products.mechanism.least_stock`),
    }
    return mechanismMap[mechanism] || mechanism
  }

  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        nameKey: `shared.flexible_products.list.columns.name`,
        persist: true,
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ id, name }: { id: string; name: string }) => {
          const urlPrefix = role === 'operator' ? '/operator' : '/admin'
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Link to={`/flexible_products/${id}`} style={{ wordBreak: 'break-word' }}>
                {name}
              </Link>
              <a
                href={`${urlPrefix}/flexible_products/${id}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0 }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          )
        },
      },
      {
        nameKey: `shared.flexible_products.list.columns.sku`,
        sortable: true,
        sortFieldName: 'flexible_products.sku',
        wrap: true,
        selector: ({ sku }: { sku: string }) => sku,
      },
      {
        name: (
          <Space size={4}>
            {t('shared.flexible_products.list.columns.mechanism')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.flexible_products.list.columns.mechanism_tooltip')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '150px',
        wrap: true,
        cell: ({ mechanism }: { mechanism: string }) => (
          <Tag color="blue">
            {getMechanismLabel(mechanism)}
          </Tag>
        ),
      },
      {
        name: (
          <Space size={4}>
            {t('shared.flexible_products.list.columns.products')}
            <Tooltip title={t('shared.flexible_products.list.columns.products_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '300px',
        wrap: true,
        cell: ({ products }: { products: ProductRef[] | null | undefined }) => <ProductsCell products={products} />,
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'flexible_products.id',
        wrap: true,
        selector: ({ created_at }: { created_at: string | null }) => created_at,
      }
    ]
    // Operator: 在 SKU 後面添加 Merchant 欄位
    if (role === 'operator' && merchants && merchants.length > 0) {
      baseColumns.splice(2, 0, {
        nameKey: `shared.flexible_products.list.columns.merchant`,
        sortable: false,
        wrap: true,
        selector: ({ merchant_id }: { merchant_id: string }) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      })
    }
    return baseColumns
  }, [role, merchants, t])

  const supportRules = useMemo(() => {
    const baseRules: any[] = [
      {
        label: t(`shared.flexible_products.list.filters.sku`),
        component: MultipleValue,
        inputName: 'flexible_products.sku',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.flexible_products.list.filter_tooltips.sku.title'),
          content: t('shared.flexible_products.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t(`shared.flexible_products.list.filters.name`),
        component: MultipleValue,
        inputName: 'flexible_products.name',
        createable: true,
        tooltip: {
          title: t('shared.flexible_products.list.filter_tooltips.name.title'),
          content: t('shared.flexible_products.list.filter_tooltips.name.content'),
        },
      },
    ]
    // Operator: 添加 Merchant 篩選器
    if (role === 'operator' && merchants && merchants.length > 0) {
      baseRules.splice(1, 0, {
        label: t(`shared.flexible_products.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'flexible_products.merchant_id',
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.flexible_products.list.filter_tooltips.merchant.title'),
          content: t('shared.flexible_products.list.filter_tooltips.merchant.content'),
        },
      })
    }
    return baseRules
  }, [t, role, merchants])

  const actions = useMemo<BatchAction[]>(() => ([
    {
      name: 'common.button.delete',
      danger: true,
      requiredPermission: 'products:write',
      do: (selected) => {
        const selectedIds = Array.from(selected)
        Modal.confirm({
          title: t('common.confirm.delete_title'),
          content: t('common.confirm.delete_content', {
            name: t('common.table.selected_records', { count: selectedIds.length })
          }),
          okText: t('common.button.confirm'),
          cancelText: t('common.button.cancel'),
          okButtonProps: { danger: true },
          onOk: async () => {
            try {
              await Promise.all(selectedIds.map((id) => service.delete(id)))
              message.success(t('common.success.deleted'))
              setLastModified(Date.now())
            } catch (error: any) {
              message.error(error?.message || t('common.error.delete_failed'))
            }
          }
        })
      }
    }
  ] satisfies BatchAction[]).filter((action) => !action.requiredPermission || can(action.requiredPermission)),
  [t, service, can])

  return (
    <TableIndex
      columns={columns}
      searchable={true}
      columnsEditable={true}
      tableId={tableId}
      defaultQuery={{
        sorting_field: 'flexible_products.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.flexible_products.list.filter_help_example')}
      filterDocsUrl={`/docs/${role}/flexible_products/list#features`}
      searchTooltipContent={t('shared.flexible_products.list.search_tooltip')}
    />
  )
}

export default FlexibleProductsListView;
